import { createId } from '@paralleldrive/cuid2';
import { WritableDraft } from 'immer';
import { ImmerReducer, useImmerReducer } from 'use-immer';

import { actionButtonsBlock, actionInputFormBlock, isActionInputFormBlock, isMarkdownTextBlock, markdownTextBlock } from '../blocks/utils';
import {
  ActionInputs,
  ActionInput,
  ActionType,
  Answer,
  AnswerAttachment,
  AnswerBlock,
  FeedbackResponse,
  JSONSchema,
  SearchResult,
} from '../types';

// Action types
export const AnswersActions = {
  SET_ANSWERS: 'answers/set_answers',
  CLEAR_ALL: 'answers/clear_all',
  APPEND_NEW_ANSWER: 'answers/create_empty_answer',
  APPEND_BLOCK: 'answers/append_block',
  APPEND_ANSWER_CHUNK: 'answers/append_stream_chunk',
  SET_SEARCH_RESULTS: 'answers/update_search_results',
  SET_FOLLOW_UP_QUESTIONS: 'answers/set_follow_up_questions',
  COMPLETE_ANSWER: 'answers/finish_streaming',
  SET_ANSWER_ERROR: 'answers/set_error_state',
  SET_USER_FEEDBACK: 'answers/set_user_feedback',
  CLEAR_USER_FEEDBACK: 'answers/clear_user_feedback',
  MARK_AS_UNCERTAIN: 'answers/mark_as_uncertain',
  NO_ARTICLES_FOUND: 'answers/no_articles_found',
  // action flow
  GET_ACTION_INPUTS: 'answers/request_action_inputs',
  INVOKE_ACTION: 'answers/invoke_action',
  ADD_ACTION_BUTTONS: 'answers/add_action_buttons',
  // knowledge group clarification
  SET_AWAITING_KNOWLEDGE_GROUP: 'answers/set_awaiting_knowledge_group',
  SET_KNOWLEDGE_GROUP_SELECTED: 'answers/set_knowledge_group_selected',
} as const;

// Action type definitions
type SetAnswers = {
  type: typeof AnswersActions.SET_ANSWERS;
  payload: {
    answers: Answer[];
  };
};

type ClearAll = {
  type: typeof AnswersActions.CLEAR_ALL;
};

type AppendNewAnswer = {
  type: typeof AnswersActions.APPEND_NEW_ANSWER;
  payload: {
    id?: string;
    query: string;
    attachments?: AnswerAttachment[];
  };
};

type SetSearchResults = {
  type: typeof AnswersActions.SET_SEARCH_RESULTS;
  payload: {
    searchQueryId: string;
    results: SearchResult[];
    index?: number;
    state?: Answer['state'];
  };
};

type AppendAnswerChunk = {
  type: typeof AnswersActions.APPEND_ANSWER_CHUNK;
  payload: {
    text: string;
    index?: number;
  };
};

type SetAnswerError = {
  type: typeof AnswersActions.SET_ANSWER_ERROR;
  payload: {
    error: string;
    index?: number;
  };
};

type SetUserFeedback = {
  type: typeof AnswersActions.SET_USER_FEEDBACK;
  payload: {
    feedback: FeedbackResponse;
    index?: number;
  };
};

type ClearUserFeedback = {
  type: typeof AnswersActions.CLEAR_USER_FEEDBACK;
  payload: {
    index?: number;
  };
};

type SetFollowUpQuestions = {
  type: typeof AnswersActions.SET_FOLLOW_UP_QUESTIONS;
  payload: {
    questions: string[];
    index?: number;
  };
};

type AppendBlock = {
  type: typeof AnswersActions.APPEND_BLOCK;
  payload: {
    block: AnswerBlock;
    index?: number;
  };
};

type CompleteAnswer = {
  type: typeof AnswersActions.COMPLETE_ANSWER;
  payload: {
    index?: number;
  };
};

type MarkAsUncertain = {
  type: typeof AnswersActions.MARK_AS_UNCERTAIN;
  payload: {
    uncertaintyText: string;
    index?: number;
  };
};

type GetActionInputs = {
  type: typeof AnswersActions.GET_ACTION_INPUTS;
  payload: {
    searchIntentId: string;
    actionId: string;
    inputs: ActionInput[];
    schema: JSONSchema<ActionInputs>;
    initialValues: ActionInputs;
    onSubmit: (parameters: ActionInputs) => void;
    index?: number;
  };
};

type AddActionButtons = {
  type: typeof AnswersActions.ADD_ACTION_BUTTONS;
  payload: {
    searchIntentId: string;
    actions: Array<{
      id: string;
      type: string;
      action: {
        version: string;
        options: {
          title?: string;
          [key: string]: any;
        };
      };
    }>;
    index?: number;
  };
};

type InvokeAction = {
  type: typeof AnswersActions.INVOKE_ACTION;
  payload: {
    index?: number;
  };
};

type NoArticlesFound = {
  type: typeof AnswersActions.NO_ARTICLES_FOUND;
  payload: {
    index?: number;
  };
};

type SetAwaitingKnowledgeGroup = {
  type: typeof AnswersActions.SET_AWAITING_KNOWLEDGE_GROUP;
  payload: {
    index?: number;
  };
};

type SetKnowledgeGroupSelected = {
  type: typeof AnswersActions.SET_KNOWLEDGE_GROUP_SELECTED;
  payload: {
    index?: number;
  };
};

export type AnswerListAction =
  | SetAnswers
  | ClearAll
  | AppendNewAnswer
  | SetSearchResults
  | AppendAnswerChunk
  | SetAnswerError
  | SetUserFeedback
  | ClearUserFeedback
  | SetFollowUpQuestions
  | AppendBlock
  | CompleteAnswer
  | MarkAsUncertain
  | InvokeAction
  | GetActionInputs
  | AddActionButtons
  | NoArticlesFound
  | SetAwaitingKnowledgeGroup
  | SetKnowledgeGroupSelected;

// Action creators
export const setAnswers = ({ answers }: { answers: Answer[] }): SetAnswers => ({
  type: AnswersActions.SET_ANSWERS,
  payload: { answers },
});

export const clearAll = (): ClearAll => ({
  type: AnswersActions.CLEAR_ALL,
});

export const appendNewAnswer = ({
  id,
  query,
  attachments,
}: {
  id?: string;
  query: string;
  attachments?: AnswerAttachment[];
}): AppendNewAnswer => ({
  type: AnswersActions.APPEND_NEW_ANSWER,
  payload: { id, query, attachments },
});

export const setSearchResults = ({
  searchQueryId,
  results,
  index,
  state,
}: {
  searchQueryId: string;
  results: SearchResult[];
  index?: number;
  state?: Answer['state'];
}): SetSearchResults => ({
  type: AnswersActions.SET_SEARCH_RESULTS,
  payload: {
    searchQueryId,
    results,
    index,
    state,
  },
});

export const appendAnswerChunk = ({ text, index }: { text: string; index?: number }): AppendAnswerChunk => ({
  type: AnswersActions.APPEND_ANSWER_CHUNK,
  payload: {
    text,
    index,
  },
});

export const setAnswerError = ({ error, index }: { error: string; index?: number }): SetAnswerError => ({
  type: AnswersActions.SET_ANSWER_ERROR,
  payload: {
    error,
    index,
  },
});

export const setUserFeedback = ({ feedback, index }: { feedback: FeedbackResponse; index?: number }): SetUserFeedback => ({
  type: AnswersActions.SET_USER_FEEDBACK,
  payload: {
    feedback,
    index,
  },
});

export const clearUserFeedback = ({ index }: { index?: number } = {}): ClearUserFeedback => ({
  type: AnswersActions.CLEAR_USER_FEEDBACK,
  payload: {
    index,
  },
});

export const setFollowUpQuestions = ({ questions, index }: { questions: string[]; index?: number }): SetFollowUpQuestions => ({
  type: AnswersActions.SET_FOLLOW_UP_QUESTIONS,
  payload: {
    questions,
    index,
  },
});

export const appendBlock = ({ block, index }: { block: AnswerBlock; index?: number }): AppendBlock => ({
  type: AnswersActions.APPEND_BLOCK,
  payload: {
    block,
    index,
  },
});

export const completeAnswer = ({ index }: { index?: number } = {}): CompleteAnswer => ({
  type: AnswersActions.COMPLETE_ANSWER,
  payload: {
    index,
  },
});

export const markAsUncertain = ({ uncertaintyText, index }: { uncertaintyText: string; index?: number }): MarkAsUncertain => ({
  type: AnswersActions.MARK_AS_UNCERTAIN,
  payload: {
    uncertaintyText,
    index,
  },
});

export const invokeAction = ({ index }: { index?: number } = {}): InvokeAction => ({
  type: AnswersActions.INVOKE_ACTION,
  payload: {
    index,
  },
});

export const noArticlesFound = ({ index }: { index?: number } = {}): NoArticlesFound => ({
  type: AnswersActions.NO_ARTICLES_FOUND,
  payload: {
    index,
  },
});

export const setAwaitingKnowledgeGroup = ({ index }: { index?: number } = {}): SetAwaitingKnowledgeGroup => ({
  type: AnswersActions.SET_AWAITING_KNOWLEDGE_GROUP,
  payload: {
    index,
  },
});

export const setKnowledgeGroupSelected = ({ index }: { index?: number } = {}): SetKnowledgeGroupSelected => ({
  type: AnswersActions.SET_KNOWLEDGE_GROUP_SELECTED,
  payload: {
    index,
  },
});

export const getActionInputs = ({
  searchIntentId,
  actionId,
  inputs,
  schema,
  initialValues,
  onSubmit,
  index,
}: {
  searchIntentId: string;
  actionId: string;
  inputs: ActionInput[];
  schema: JSONSchema<ActionInputs>;
  initialValues: ActionInputs;
  onSubmit: (parameters: ActionInputs) => void;
  index?: number;
}): GetActionInputs => ({
  type: AnswersActions.GET_ACTION_INPUTS,
  payload: {
    searchIntentId,
    actionId,
    inputs,
    schema,
    initialValues,
    onSubmit,
    index,
  },
});

export const addActionButtons = ({
  searchIntentId,
  actions,
  index,
}: {
  searchIntentId: string;
  actions: Array<{
    id: string;
    type: string;
    action: {
      version: string;
      options: {
        title?: string;
        [key: string]: any;
      };
    };
  }>;
  index?: number;
}): AddActionButtons => ({
  type: AnswersActions.ADD_ACTION_BUTTONS,
  payload: {
    searchIntentId,
    actions,
    index,
  },
});

const getTargetAnswer = (draft: WritableDraft<Answer>[], index?: number) => {
  return draft.at(index ?? -1);
};

const getLastTextBlock = (answer: Answer) => {
  const textBlocks = answer.blocks.filter(isMarkdownTextBlock);
  const lastTextBlock = textBlocks.at(-1);

  return lastTextBlock;
};

const initialState: Answer[] = [];

const reducer: ImmerReducer<Answer[], AnswerListAction> = (draft, action) => {
  switch (action.type) {
    // Answer List Management
    case AnswersActions.SET_ANSWERS: {
      draft.length = 0;
      // @ts-expect-error
      draft.push(...action.payload.answers);

      return;
    }

    case AnswersActions.CLEAR_ALL: {
      draft.length = 0;

      return;
    }

    // Answer Generation Flow
    case AnswersActions.APPEND_NEW_ANSWER: {
      draft.push({
        id: 'pending-' + createId(),
        state: 'fetching-search-results',
        question: action.payload.query,
        blocks: [markdownTextBlock()],
        isUncertain: false,
        attachments: action.payload.attachments,
      });

      return;
    }

    case AnswersActions.SET_SEARCH_RESULTS: {
      const answer = getTargetAnswer(draft, action.payload.index);
      if (answer) {
        answer.searchQueryId = action.payload.searchQueryId;
        answer.searchResults = action.payload.results;
        answer.state = action.payload.state || 'fetching-stream';
      }

      return;
    }

    case AnswersActions.APPEND_ANSWER_CHUNK: {
      const answer = getTargetAnswer(draft, action.payload.index);
      const block = answer && getLastTextBlock(answer);

      if (answer && !answer.isUncertain && block && isMarkdownTextBlock(block)) {
        answer.state = 'streaming';
        block.text += action.payload.text;
      }

      return;
    }

    case AnswersActions.SET_ANSWER_ERROR: {
      const answer = getTargetAnswer(draft, action.payload.index ?? -1);
      const block = answer && getLastTextBlock(answer);
      if (block && isMarkdownTextBlock(block)) {
        block.text = action.payload.error; // Replace loading text with error message
        answer.state = 'completed'; // Mark as completed so UI doesn't show loading state
        answer.hasError = true; // Flag to indicate this answer has an error
      }

      return;
    }

    case AnswersActions.APPEND_BLOCK: {
      const answer = getTargetAnswer(draft, action.payload.index);
      if (answer) {
        // @ts-expect-error
        answer.blocks.push(action.payload.block);
      }

      return;
    }

    case AnswersActions.COMPLETE_ANSWER: {
      const answer = getTargetAnswer(draft, action.payload.index);
      if (answer) {
        // Mark as completed regardless of current state to ensure loading states end
        // This handles errors during fetching-search-results, fetching-stream, or streaming
        const shouldComplete =
          answer.state === 'streaming' || answer.state === 'fetching-search-results' || answer.state === 'fetching-stream';
        if (shouldComplete) {
          answer.state = 'completed';
        }
      }

      return;
    }

    case AnswersActions.MARK_AS_UNCERTAIN: {
      const answer = getTargetAnswer(draft, action.payload.index);
      const block = answer && getLastTextBlock(answer);
      if (answer && block && isMarkdownTextBlock(block)) {
        answer.isUncertain = true;
        block.text = action.payload.uncertaintyText;
      }

      return;
    }

    case AnswersActions.SET_USER_FEEDBACK: {
      const answer = getTargetAnswer(draft, action.payload.index);
      if (answer) {
        answer.feedback = action.payload.feedback;
      }

      return;
    }

    case AnswersActions.CLEAR_USER_FEEDBACK: {
      const answer = getTargetAnswer(draft, action.payload.index);
      if (answer) {
        delete answer.feedback;
      }

      return;
    }

    case AnswersActions.GET_ACTION_INPUTS: {
      const answer = getTargetAnswer(draft, action.payload.index);
      if (answer) {
        answer.state = 'getting-action-inputs';
        answer.searchIntentId = action.payload.searchIntentId;
        answer.blocks.push(
          // @ts-expect-error
          actionInputFormBlock({
            actionId: action.payload.actionId,
            schema: action.payload.schema,
            initialValues: action.payload.initialValues,
            onSubmit: action.payload.onSubmit,
            inputs: action.payload.inputs,
          }),
        );
      }

      return;
    }

    case AnswersActions.ADD_ACTION_BUTTONS: {
      const answer = getTargetAnswer(draft, action.payload.index);
      if (answer) {
        answer.searchIntentId = action.payload.searchIntentId;
        // Cast the actions array to ActionType to match the expected type
        answer.actions = action.payload.actions.map((action) => ({
          ...action,
          type: action.type as ActionType,
        }));
        answer.blocks.push(actionButtonsBlock());
      }

      return;
    }

    case AnswersActions.INVOKE_ACTION: {
      const answer = getTargetAnswer(draft, action.payload.index);
      const blockIndex = answer?.blocks.findIndex(isActionInputFormBlock);

      if (answer && blockIndex) {
        // replace the action input form block with a markdown text block
        answer.state = 'fetching-stream';
        answer.blocks.splice(blockIndex, 1, markdownTextBlock());
      }

      return;
    }

    case AnswersActions.SET_FOLLOW_UP_QUESTIONS: {
      const answer = getTargetAnswer(draft, action.payload.index);
      if (answer) {
        answer.followUpQuestions = action.payload.questions;
      }

      return;
    }

    case AnswersActions.NO_ARTICLES_FOUND: {
      const answer = getTargetAnswer(draft, action.payload.index);
      const block = answer && getLastTextBlock(answer);
      if (block && isMarkdownTextBlock(block)) {
        block.text += 'No articles found.';
      }
      // Mark the answer as completed when no articles are found
      if (answer) {
        answer.state = 'completed';
      }

      return;
    }

    case AnswersActions.SET_AWAITING_KNOWLEDGE_GROUP: {
      const answer = getTargetAnswer(draft, action.payload.index);
      if (answer) {
        answer.state = 'awaiting-knowledge-group';
      }

      return;
    }

    case AnswersActions.SET_KNOWLEDGE_GROUP_SELECTED: {
      const answer = getTargetAnswer(draft, action.payload.index);
      if (answer) {
        answer.state = 'fetching-search-results';
      }

      return;
    }

    default: {
      throw new Error(`Answer List Reducer: unknown action type '${(action as AnswerListAction).type}'`);
    }
  }
};

export const useAnswerList = () => {
  return useImmerReducer(reducer, initialState);
};
